// Country registry for the web funnel.
// Each country owns its LPs (marketing, src/funnelWeb/landings/<dir>) and a
// funnel (dev, src/funnelWeb/funnels/<dir>). URL: /funnel/<code>/<angle> with a
// short country CODE (br, fr, …) and angle slugs in the country's own language.
// Add a country: add its code here + create the two folders + fill the tables.

import { hasOwn } from 'src/utils/hasOwn';

export const COUNTRY_SLUGS = ['br', 'fr'] as const;
export type CountrySlug = (typeof COUNTRY_SLUGS)[number];

export const DEFAULT_COUNTRY: CountrySlug = 'br';

// Historical URLs (live campaigns, old share links) used the long country name.
// resolveFunnel maps them to the code so those links keep working; FunnelWeb
// then canonicalizes the address bar to the code form before mounting.
export const LEGACY_COUNTRY_ALIASES: Record<string, CountrySlug> = {
  brazil: 'br',
};

export const isCountrySlug = (value: string): value is CountrySlug =>
  (COUNTRY_SLUGS as readonly string[]).includes(value);

/**
 * Resolve the country + router basename from the current path.
 *
 * A country is taken from the URL ONLY when the first segment after `/funnel/` is
 * a known code (allowlist) or a legacy alias. Everything else under `/funnel` —
 * the bare `/funnel`, `/funnel/funnel-return`, `/funnel/select-type`, legacy deep
 * links — keeps the historical basename `/funnel` and the default country, so
 * existing ad and RevenueCat return URLs hardcoded to `/funnel...` never break.
 * The allowlist can't collide with funnel route segments (select-type, …).
 */
export const resolveFunnel = (
  pathname: string,
): { country: CountrySlug; basename: string } => {
  const match = pathname.match(/^\/funnel\/([^/?#]+)/);
  if (match && isCountrySlug(match[1])) {
    return { country: match[1], basename: `/funnel/${match[1]}` };
  }
  if (match && hasOwn(LEGACY_COUNTRY_ALIASES, match[1])) {
    // Only reachable if the address bar still shows the legacy segment (the
    // canonical rewrite happens before the router mounts; this keeps resolve
    // correct in between).
    return {
      country: LEGACY_COUNTRY_ALIASES[match[1]],
      basename: `/funnel/${match[1]}`,
    };
  }
  return { country: DEFAULT_COUNTRY, basename: '/funnel' };
};

/**
 * Canonical form of a /funnel URL: legacy country aliases become their code
 * (`/funnel/brazil/...` → `/funnel/br/...`). Returns null when already canonical.
 */
export const canonicalFunnelPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/funnel\/([^/?#]+)(.*)$/);
  if (!match || !hasOwn(LEGACY_COUNTRY_ALIASES, match[1])) return null;
  return `/funnel/${LEGACY_COUNTRY_ALIASES[match[1]]}${match[2]}`;
};

// Countries whose funnel needs cookie consent before any ad tracker or
// identifier is set (CNIL in France). The inline gtag guard in index.html
// hardcodes the same list as a path regex: keep both in sync.
export const CONSENT_COUNTRIES: readonly CountrySlug[] = ['fr'];

export const requiresConsent = (country: CountrySlug): boolean =>
  CONSENT_COUNTRIES.includes(country);

/** True for any web funnel URL (`/funnel` and everything under it). */
export const isFunnelPath = (pathname: string): boolean =>
  pathname === '/funnel' || pathname.startsWith('/funnel/');

